// The three-figure header both revenue surfaces open on: the /analysis card
// (RevenueVsBudgetPanel) and the full report (RevenueVsExpensesReportPage).
//
// It exists as ONE component because it was briefly two, and the two disagreed
// within a week — the card and the report read different fields for the same
// label, one tap apart. A shared component makes that class of drift
// impossible rather than merely fixed once.
//
// הוצאות (ללא מע״מ) is `spend_net_total`: mapped spend on COUNTED days — days in
// the range with revenue entered or marked closed (owner, 2026-09-22; see
// countedDays in server/lib/revenue.js). It is what the % divides.
// What was left out (`excluded_spend_net`) is NOT on the card face: both a
// grey line under the tiles and a sub-line in the הוצאות tile read as one note
// too many (owner, 2026-09-22). It lives in the ⋮ menu under "חשוב לדעת",
// right after "איך מחושב" — ExcludedSpendNote below, shared by both menus.
// `total_purchases_net`, the figure that ties to /analysis's סה״כ רכש, is not
// shown on screen; the Excel export carries it.
//
// ONE NUMBER SIZE for every figure here — see .pct-figure. Rank between the three
// is carried by label and container, never by size.
// The figure treatment is a CLASS, not an inline style, for one reason: it has
// to change size at desktop and inline styles cannot carry a media query. The
// headline tile and the category rows both wear it, so they can only ever move
// together — which is the whole point of the "one card, one number size" rule.
// 16px was sized for a 111px tile at 375px; at desktop the tile is capped at
// 300px and the figure goes to 20px (see .pct-figure in index.css).
import { formatCurrencyWhole } from '../utils/api';

export const PCT_CLASS = 'pct-figure';

const SUB_STYLE = { fontSize: 11.5, color: '#94a3b8' };

/**
 * @param {object}   totals      the `totals` block of /api/revenue/summary or /report
 * @param {string}   money       formatter for the two ₪ figures (formatCurrencyWhole)
 * @param {boolean}  showGross   the report only: the הכנסות tile's sub-line also
 *                               carries the gross Z-report figure
 */
export default function RevenueMetricTiles({ totals, money, showGross = false }) {
  const tiles = [
    {
      label: 'הכנסות',
      value: money(totals.revenue_net),
      // On the report the gross figure the owner typed off the Z-report lives
      // HERE, beside its own net — it floated under the tiles and could have
      // belonged to any of them (owner, after a design + product review).
      // On a phone it wraps only after the "·" — the gross half is one unit.
      sub: showGross
        ? <>ללא מע״מ · <span style={{ whiteSpace: 'nowrap' }}>{formatCurrencyWhole(totals.revenue)} כולל מע״מ</span></>
        : '(ללא מע״מ)',
      shekel: true,
    },
    { label: 'הוצאות', value: money(totals.spend_net_total), sub: '(ללא מע״מ)', shekel: true },
    {
      label: 'Food Cost',
      value: totals.food_cost_pct != null ? `${totals.food_cost_pct}%` : '-',
      sub: totals.food_cost_line_count === 0
        ? 'לא סומנו קטגוריות'
        : `${formatCurrencyWhole(totals.food_cost_spend_net ?? 0)} (ללא מע״מ)`,
    },
  ];
  return (
    <div className="metric-tiles">
      {tiles.map(t => (
        <div key={t.label} className="metric-tile">
          <div className="metric-tile__label">{t.label}</div>
          {/* ₪ figures run RIGHT-TO-LEFT so the sign follows the number the
              Hebrew way ("147,481.85 ₪"), matching the category rows (owner).
              .pct-figure forces ltr for percentages, so the ₪ tiles override
              it inline — a dir attribute alone loses to the class. */}
          <div
            dir={t.shekel ? 'rtl' : 'ltr'}
            className={PCT_CLASS}
            style={{ textAlign: 'right', color: '#1a1a2e', ...(t.shekel ? { direction: 'rtl' } : null) }}
          >
            {t.value}
          </div>
          {/* Every tile keeps a third line even when it has nothing to say:
              the ₪ tiles spend it on their VAT basis and a percentage has none,
              so the nbsp keeps the three the same height — and what
              stops the header reflowing when a flag appears between periods. */}
          <div style={SUB_STYLE}>{t.sub || ' '}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * The "חשוב לדעת" section of both ⋮ menus: how much purchasing the counted-days
 * rule left out. Renders only when that is POSITIVE: credits dated on an
 * excluded day can push it below zero, and "רכש בסך -300 ₪ … לא נכלל" says
 * nothing a reader can use. Not wrapped in dir="ltr":
 * formatCurrencyWhole already lays the amount out for Hebrew, ₪ after the number.
 */
export function ExcludedSpendNote({ totals, style }) {
  const excluded = Number(totals?.excluded_spend_net) || 0;
  const unmapped = Number(totals?.unmapped_spend_net) || 0;
  if (excluded <= 0 && unmapped <= 0) return null;
  return (
    <div style={style}>
      <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>חשוב לדעת</div>
      {excluded > 0 && <p className="revenue-calculation-note">
        רכש בסך <b>{formatCurrencyWhole(excluded)}</b> מימים ללא הכנסות לא נכלל בחישוב.
      </p>}
      {unmapped > 0 && <p className="revenue-calculation-note">
        רכש בסך <b>{formatCurrencyWhole(unmapped)}</b> ללא שיוך לקטגוריה לא נכלל בחישוב.
      </p>}
    </div>
  );
}

/**
 * A formatIsoRange() string laid out for a Hebrew line. The range used to be
 * wrapped whole in dir="ltr", which put the month FIRST and read "בספט׳ 4–1".
 * Right-to-left is correct for the words; only the "1–4" day pair must run
 * left-to-right, or the dash between two numbers flips them. So just that pair
 * is isolated.
 */
function rtlRange(range) {
  const m = /^(\d+–\d+)(.*)$/.exec(range);
  if (!m) return range;
  return <><bdi dir="ltr">{m[1]}</bdi>{m[2]}</>;
}

/**
 * Renders an entryFrontierLine() result — grey provenance, never amber; darker
 * and with a heavier date range once it is stale. `style` carries the
 * container-specific bits (size, layout).
 */
export function EntryFrontierText({ line, style }) {
  return (
    <span style={{
      lineHeight: 1.5, fontWeight: 400,
      color: line.stale ? '#475569' : '#8a94a3',
      ...style,
    }}>
      {line.before}
      {line.range && (
        <span style={{ fontWeight: line.stale ? 600 : 500 }}>{rtlRange(line.range)}</span>
      )}
      {line.after}
    </span>
  );
}
